import { Modal } from '@components/utils/Modal';
import {
  type Dashboard,
  type DuplicateRequest,
  type RequestGroupConversations,
} from '@/types/dashboard';
import { dashboardService } from '@services/dashboardService';
import { useState } from 'react';
import { useSocketConnectionId } from '@contexts/SocketContext';
import ChangeCardsProject from './ChangeCardsProject';
import CorgeeStorageHelper from '@/components/Globals/CorgeeStorageHelper';

interface DuplicateModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dashboard?: Dashboard;
  isProjectDetail?: boolean;
  onDuplicated?: (newDashboardId?: number) => void;
}

interface ProjectRequest {
  ConfigValueId: number;
  Filters: any[];
  IsFromProject: boolean;
  ProjectName: string;
  GroupByLayerId: number;
  GroupByDetailId: number;
  GroupByValueField: string;
  ProjectId?: number;
  IsPreview?: number;
  configValueName?: string;
  ConversationsIds?: string;
  SocketConnectionId?: string;
}

export const DuplicateModal: React.FC<any> = ({
  open,
  setOpen,
  dashboard,
  isProjectDetail,
  onDuplicated,
}: DuplicateModalProps) =>
{
  const webUrl = import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/';
  const socketConnectionId = useSocketConnectionId();

  const [loadingComplete, setLoadingComplete] = useState(false);
  const [loadingDuplicate, setLoadingDuplicate] = useState(false);
  const [duplicateCompleteMode, setDuplicateCompleteMode] = useState(false);
  const [requestChangeCard, setRequestChangeCard] = useState<ProjectRequest | null>();
  const [dashboardContext, setDashboardContext] = useState<any>();
  const [setUrl, setSetUrl] = useState('');
  const duplicateDashboard = async (option: number) =>
  {
    const request: DuplicateRequest = {
      DuplicateDashboardOption: option,
      DashboardId: dashboard?.DashboardId || 0,
      isFromProject: (dashboard?.ProjectId || 0) > 0,
      Project: dashboard?.ProjectId?.toString(),
      LayersToHide: '',
      PageNumber: 1,
      PageSize: 300,
      Filters: '[]',
      CustomFields: [],
      SocketConnectionId: socketConnectionId,
    };
    setLoadingComplete(true);
    const newDashboard = await dashboardService.DuplicateCompleteDashboard(request);
    onDuplicated?.(newDashboard?.DashboardId);
    setLoadingComplete(false);
    setLoadingDuplicate(false);
    setDuplicateCompleteMode(false);
    reset();
  };

  const duplicateCompleteDashboard = async () =>
  {
    setLoadingDuplicate(true);
    setDuplicateCompleteMode(true);
    const requestInitialDashbaord: RequestGroupConversations = {
      CustomFields: [],
      PageNumber: 1,
      PageSize: 130,
      DashboardId: dashboard?.DashboardId || 0,
      Project: dashboard?.ProjectId?.toString() || null,
      Filters: '[]',
      SearchText: '',
      LayersToHide: '',
      DashboardViewTypeId: 1,
      IsFilterPersonal: true,
    };
    const initialDashbaord =
      await dashboardService.getInitialDashboardGroupConversations(requestInitialDashbaord);
    setLoadingDuplicate(false);
    const conversationIds = initialDashbaord
      .filter(group => !group.IsDefault)
      .flatMap(r => r.Conversations)
      .map(c => c.ConversationId)
      .join();
    let configProjectName = 'Projects';
    let configValueName = '';
    const tmpProjectConfigValueId = CorgeeStorageHelper.getProjectNameConfig();
    if (tmpProjectConfigValueId !== undefined && tmpProjectConfigValueId !== null)
    {
      configProjectName = tmpProjectConfigValueId;
    }

    configValueName = configProjectName;
    if (configProjectName.endsWith('s'))
    {
      configValueName = configProjectName.substring(0, configProjectName.length - 1);
    }

    const request: ProjectRequest = {
      ConfigValueId: 9,
      Filters: [],
      IsFromProject: false,
      ProjectName: '',
      GroupByLayerId: 0,
      GroupByDetailId: 0,
      GroupByValueField: '',
      ConversationsIds: conversationIds,
      configValueName: configValueName,
      ProjectId: dashboard?.ProjectId || 0,
      SocketConnectionId: socketConnectionId,
    };
    setDashboardContext(initialDashbaord);
    setRequestChangeCard(request);
  };

  const onCloseDuplicate = (goToDuplicateMenu?: boolean, closeModal?: boolean) =>
  {
    if (closeModal)
    {
      setOpen(false);
    }

    if (goToDuplicateMenu && !closeModal)
    {
      setDuplicateCompleteMode(false);
    }
  };

  const reset = () =>
  {
    setOpen(false);
    setTimeout(() =>
    {
      setLoadingComplete(false);
      setLoadingDuplicate(false);
      setDuplicateCompleteMode(false);
      setDashboardContext(null);
      setRequestChangeCard(null);
      setSetUrl('');
    }, 400);
  };

  const onConversationsUpdated = (response: any) =>
  {
    if (response)
    {
      setDuplicateCompleteMode(false);
      setLoadingDuplicate(false);
      setLoadingComplete(true);

      setSetUrl(
        `${webUrl}#/ProjectDetails/${response.newDashboard.ProjectId}?ProjectName=undefined&ProjectDashboardId=${response.newDashboard.DashboardId}&DashboardIndex=${response.newDashboard.Order}`,
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={`${duplicateCompleteMode ? 'Duplicate Dashboard to' : 'Duplicate'}`}
      preventCloseClickOut
    >
      <div className="w-88 mt-3">
        {!duplicateCompleteMode && (
          <>
            <div className="flex flex-col items-center justify-center">
              {!loadingComplete ? (
                <>
                  <button
                    onClick={() =>
                    {
                      duplicateDashboard(1);
                    }}
                    className="bg-corgee-blue hover:bg-corgee-blueHover dark:bg-[#3c6c80] dark:hover:brightness-[0.85] text-white text-[13px] font-normal w-88 mx-0 mb-1 px-4 py-2 "
                  >
                    Dashboard Only
                  </button>
                  <button
                    onClick={() =>
                    {
                      duplicateDashboard(2);
                    }}
                    className="bg-corgee-blue hover:bg-corgee-blueHover dark:bg-[#3c6c80] dark:hover:brightness-[0.85] text-white text-[13px] font-normal w-88 mx-0 mb-1 px-4 py-2 "
                  >
                    Dashboard With Card Instances
                  </button>
                  <button
                    onClick={() =>
                    {
                      duplicateDashboard(3);
                    }}
                    className="bg-corgee-blue hover:bg-corgee-blueHover dark:bg-[#3c6c80] dark:hover:brightness-[0.85] text-white text-[13px] font-normal w-88 mx-0 mb-1 px-4 py-2 "
                  >
                    Dashboard And Cards
                  </button>
                  {isProjectDetail && (
                    <button
                      onClick={() =>
                      {
                        duplicateCompleteDashboard();
                      }}
                      className="bg-corgee-blue hover:bg-corgee-blueHover dark:bg-[#3c6c80] dark:hover:brightness-[0.85] text-white text-[13px] font-normal w-88 mx-0 mb-1  px-4 py-2 "
                    >
                      Dashboard And Cards To Another Project
                    </button>
                  )}
                </>
              ) : (
                <p className="text-corgee-black dark:text-[#a9a093] leading-8">
                  {'The dashboard has been successfully duplicated \n'}
                  {setUrl.length > 0 && (
                    <a
                      href="setUrl"
                      target="_blank"
                      className="text-corgee-blue decoration-transparent"
                    >
                      Open dashboard in a new tab
                    </a>
                  )}
                </p>
              )}
            </div>
            <div className="pt-4 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
              <button
                type="button"
                onClick={() => reset()}
                className="bg-brightGray hover:bg-corgee-brightGrayHover dark:bg-[#5c6060] dark:hover:brightness-[0.85] text-white text-xs h-7 px-3 py-1"
              >
                Close
              </button>
            </div>
          </>
        )}
        {duplicateCompleteMode && !loadingDuplicate && (
          <div>
            <div
              id="select-render-cstatus-Layers"
              className="corgee_com_custom-fields-combobox-single _DEL_comboBoxRef _DEL_multiselect-hidden-control-container input-group"
            >
              <ChangeCardsProject
                request={requestChangeCard as any}
                key={'ChangeCardsProject'}
                onClose={onCloseDuplicate}
                dashboardContext={dashboardContext}
                onConversationsUpdated={onConversationsUpdated}
              />
            </div>
          </div>
        )}
        {loadingDuplicate && duplicateCompleteMode && (
          <div className="flex justify-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
